"use client";

import { c as _c } from "react/compiler-runtime";
import { useContext, useState } from "react";
import type { ApiProfileProxy } from "@/generated/models/ApiProfileProxy";
import type { ApiProfileProxyAction } from "@/generated/models/ApiProfileProxyAction";
import CommonTimeSelect from "@/components/utils/time/CommonTimeSelect";
import { AuthContext } from "@/components/auth/Auth";
import { ReactQueryWrapperContext } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { useMutation } from "@tanstack/react-query";
import { commonApiPut } from "@/services/api/common-api";
import ProxyCreateActionConfigEndTimeSwitch from "@/components/user/proxy/proxy/create-action/config/ProxyCreateActionConfigEndTimeSwitch";
import { Time } from "@/helpers/time";
import CircleLoader, { CircleLoaderSize } from "@/components/distribution-plan-tool/common/CircleLoader";
import type { ApiUpdateProxyActionRequest } from "@/generated/models/ApiUpdateProxyActionRequest";
export default function ProfileProxyEndTimeEdit(t0) {
  const $ = _c(47);
  const {
    profileProxy,
    profileProxyAction,
    setViewMode
  } = t0;
  const {
    requestAuth,
    setToast
  } = useContext(AuthContext);
  const {
    onProfileProxyModify
  } = useContext(ReactQueryWrapperContext);
  const [isEndTimeDisabled, setIsEndTimeDisabled] = useState(!profileProxyAction.end_time);
  const [endTime, setEndTime] = useState(profileProxyAction.end_time);
  const isChangedAndValid = profileProxyAction.end_time !== (isEndTimeDisabled ? null : endTime) && (isEndTimeDisabled || endTime !== null && endTime >= Time.currentMillis());
  const [submitting, setSubmitting] = useState(false);
  let t1;
  if ($[0] !== profileProxy || $[1] !== profileProxyAction.id) {
    t1 = async body => await commonApiPut({
      endpoint: `proxies/${profileProxy.id}/actions/${profileProxyAction.id}`,
      body
    });
    $[0] = profileProxy;
    $[1] = profileProxyAction.id;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== onProfileProxyModify || $[4] !== profileProxy || $[5] !== setToast || $[6] !== setViewMode) {
    t2 = () => {
      onProfileProxyModify({
        profileProxyId: profileProxy.id
      });
      setToast({
        message: "Action end time updated successfully!",
        type: "success"
      });
      setViewMode();
    };
    $[3] = onProfileProxyModify;
    $[4] = profileProxy;
    $[5] = setToast;
    $[6] = setViewMode;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  let t3;
  if ($[8] !== setToast) {
    t3 = error => {
      setToast({
        message: error as unknown as string,
        type: "error"
      });
    };
    $[8] = setToast;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      setSubmitting(false);
    };
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== t1 || $[12] !== t2 || $[13] !== t3) {
    t5 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3,
      onSettled: t4
    };
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const profileProxyActionCreditMutation = useMutation(t5);
  let t6;
  if ($[15] !== endTime || $[16] !== isEndTimeDisabled || $[17] !== profileProxyActionCreditMutation || $[18] !== requestAuth) {
    t6 = async () => {
      if (!endTime && !isEndTimeDisabled) {
        return;
      }
      setSubmitting(true);
      const {
        success
      } = await requestAuth();
      if (!success) {
        setSubmitting(false);
        return;
      }
      await profileProxyActionCreditMutation.mutateAsync({
        end_time: isEndTimeDisabled ? null : endTime
      });
    };
    $[15] = endTime;
    $[16] = isEndTimeDisabled;
    $[17] = profileProxyActionCreditMutation;
    $[18] = requestAuth;
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const onSubmit = t6;
  let t7;
  if ($[20] !== isEndTimeDisabled) {
    t7 = <div className="tw-mb-3 tw-w-full md:tw-w-auto"><ProxyCreateActionConfigEndTimeSwitch isActive={isEndTimeDisabled} setIsActive={setIsEndTimeDisabled} /></div>;
    $[20] = isEndTimeDisabled;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  let t8;
  if ($[22] !== isEndTimeDisabled || $[23] !== profileProxyAction.end_time) {
    t8 = !isEndTimeDisabled && <CommonTimeSelect currentTime={profileProxyAction.end_time} onMillis={setEndTime} disabled={isEndTimeDisabled} inline={true} size="sm" />;
    $[22] = isEndTimeDisabled;
    $[23] = profileProxyAction.end_time;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] !== t7 || $[26] !== t8) {
    t9 = <div className="tw-flex tw-flex-col md:tw-flex-row tw-gap-y-3 tw-gap-x-4 tw-w-full tw-h-full tw-items-center">{t7}{t8}</div>;
    $[25] = t7;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== setViewMode || $[29] !== submitting) {
    t10 = <button onClick={setViewMode} type="button" disabled={submitting} className="tw-w-full sm:tw-w-auto tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-flex tw-items-center tw-justify-center tw-rounded-lg tw-bg-iron-800 hover:tw-bg-iron-700 tw-border tw-border-solid tw-border-iron-700 tw-text-iron-300 hover:tw-text-iron-50 focus:tw-outline-none tw-transition tw-duration-300 tw-ease-out">Cancel</button>;
    $[28] = setViewMode;
    $[29] = submitting;
    $[30] = t10;
  } else {
    t10 = $[30];
  }
  const t11 = !isChangedAndValid || submitting;
  const t12 = `${isChangedAndValid ? "hover:tw-bg-primary-600 hover:tw-border-primary-600" : "tw-opacity-50"} tw-w-full tw-flex tw-items-center tw-justify-center tw-relative tw-bg-primary-500 tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-white tw-border tw-border-solid tw-border-primary-500 tw-rounded-lg tw-transition tw-duration-300 tw-ease-out`;
  let t13;
  if ($[31] !== submitting) {
    t13 = submitting && <CircleLoader size={CircleLoaderSize.SMALL} />;
    $[31] = submitting;
    $[32] = t13;
  } else {
    t13 = $[32];
  }
  let t14;
  if ($[33] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <span>Update</span>;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== t13) {
    t15 = <div className="tw-flex tw-items-center tw-space-x-2">{t13}{t14}</div>;
    $[34] = t13;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== onSubmit || $[37] !== t11 || $[38] !== t12 || $[39] !== t15) {
    t16 = <button onClick={onSubmit} disabled={t11} type="button" className={t12}>{t15}</button>;
    $[36] = onSubmit;
    $[37] = t11;
    $[38] = t12;
    $[39] = t15;
    $[40] = t16;
  } else {
    t16 = $[40];
  }
  let t17;
  if ($[41] !== t10 || $[42] !== t16) {
    t17 = <div className="tw-mt-5 sm:tw-mt-8 md:tw-mt-0 tw-flex tw-items-center tw-justify-end md:tw-justify-start tw-gap-x-3">{t10}{t16}</div>;
    $[41] = t10;
    $[42] = t16;
    $[43] = t17;
  } else {
    t17 = $[43];
  }
  let t18;
  if ($[44] !== t17 || $[45] !== t9) {
    t18 = <div><div className="tw-h-full tw-py-3 xl:tw-h-14 tw-rounded-lg tw-bg-iron-900 tw-ring-1 tw-ring-inset tw-ring-iron-600"><div className="tw-px-3 tw-w-full tw-h-full tw-flex tw-flex-col sm:tw-flex-row md:tw-items-center sm:tw-justify-between tw-gap-x-3">{t9}{t17}</div></div></div>;
    $[44] = t17;
    $[45] = t9;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  return t18;
}